import React, { Component } from 'react';
import Spinner from '../common/Spinner';
import { connect } from 'react-redux';
import { getInvitations, deleteInvitation, addInvitation, updateInvitation, addBulkInvitations, getInvitationFilterValues } from '../../actions/invitationActions';
import { getGroups } from '../../actions/groupActions';
import { checkEmailExists } from '../../actions/userActions';
import PropTypes from 'prop-types';
import { Filter, X, Upload, Trash2, Edit2, Mail, AtSign, Clock, Layers, ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import MultiSelectSearchFilter from '../content-management/common/MultiSelectSearchFilter';
import Papa from 'papaparse';

class InvitationsTab extends Component {
  static propTypes = {
    getInvitations: PropTypes.func.isRequired,
    getInvitationFilterValues: PropTypes.func.isRequired,
    invitation: PropTypes.object.isRequired,
    isAuthenticated: PropTypes.bool
  };

  state = {
    // Filter state
    statusFilter: [],
    roleFilter: [],
    selectedEmails: [],
    selectedGroups: [],
    startDate: '',
    endDate: '',
    // Pagination state
    currentPage: 1,
    limit: 50,
    // Modal state
    modal: false,
    editMode: false,
    editingInvitation: null,
    email: '',
    role: 'student',
    group: '',
    msg: null,
    // Email validation state
    emailError: null,
    isCheckingEmail: false,
    // Bulk invitation state
    bulkModal: false,
    csvFile: null,
    csvData: [],
    bulkResults: null,
    bulkLoading: false,
    // Delete confirmation modal state
    deleteModal: false,
    invitationToDelete: null
  };

  componentDidMount() {
    this.loadInvitations();
    this.props.getGroups();
    this.props.getInvitationFilterValues();
  }

  loadInvitations = () => {
    const { statusFilter, roleFilter, selectedEmails, selectedGroups, startDate, endDate, currentPage, limit } = this.state;

    const filters = {};
    if (statusFilter.length > 0) filters.status = statusFilter;
    if (roleFilter.length > 0) filters.role = roleFilter;
    if (selectedEmails.length > 0) filters.emails = selectedEmails;
    if (selectedGroups.length > 0) filters.groups = selectedGroups;
    if (startDate) filters.startDate = startDate;
    if (endDate) filters.endDate = endDate;

    const options = { page: currentPage, limit };

    this.props.getInvitations(filters, options);
  };

  // Filter handlers
  handleStatusChange = (statuses) => {
    this.setState({ statusFilter: statuses, currentPage: 1 }, this.loadInvitations);
  };

  handleRoleChange = (roles) => {
    this.setState({ roleFilter: roles, currentPage: 1 }, this.loadInvitations);
  };

  handleEmailChange = (emails) => {
    this.setState({ selectedEmails: emails, currentPage: 1 }, this.loadInvitations);
  };

  handleGroupChange = (groups) => {
    this.setState({ selectedGroups: groups, currentPage: 1 }, this.loadInvitations);
  };

  handleDateChange = (e) => {
    this.setState({ [e.target.name]: e.target.value }, () => {
      // Only reload if both dates are set or both are empty
      if ((this.state.startDate && this.state.endDate) || (!this.state.startDate && !this.state.endDate)) {
        this.setState({ currentPage: 1 }, this.loadInvitations);
      }
    });
  };

  clearAllFilters = () => {
    this.setState({
      statusFilter: [],
      roleFilter: [],
      selectedEmails: [],
      selectedGroups: [],
      startDate: '',
      endDate: '',
      currentPage: 1
    }, this.loadInvitations);
  };

  // Pagination handlers
  handlePageChange = (newPage) => {
    this.setState({ currentPage: newPage }, this.loadInvitations);
  };

  onDeleteClick = id => {
    this.setState({
      deleteModal: true,
      invitationToDelete: id
    });
  };

  handleDeleteConfirm = () => {
    this.props.deleteInvitation(this.state.invitationToDelete);
    this.setState({
      deleteModal: false,
      invitationToDelete: null
    });
  };

  handleDeleteCancel = () => {
    this.setState({
      deleteModal: false,
      invitationToDelete: null
    });
  };

  onEditClick = (invitation) => {
    this.setState({
      modal: true,
      editMode: true,
      editingInvitation: invitation,
      email: invitation.email,
      role: invitation.role,
      group: invitation.group ? invitation.group._id : '',
      msg: null
    });
  };

  // Modal methods
  toggle = () => {
    this.setState({
      modal: !this.state.modal,
      editMode: false,
      editingInvitation: null,
      msg: null,
      email: '',
      role: 'student',
      group: '',
      emailError: null,
      isCheckingEmail: false
    });
  };

  // Bulk invitation methods
  toggleBulkModal = () => {
    this.setState({
      bulkModal: !this.state.bulkModal,
      csvFile: null,
      csvData: [],
      bulkResults: null,
      bulkLoading: false
    });
  };

  handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'text/csv') {
      this.setState({ csvFile: file });
      
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (result) => {
          if (result.errors.length > 0) {
            this.setState({ 
              msg: 'Error parsing CSV file: ' + result.errors[0].message 
            });
            return;
          }

          const requiredColumns = ['Email Address', 'Role', 'Group'];
          const headers = Object.keys(result.data[0] || {});
          const missingColumns = requiredColumns.filter(col => !headers.includes(col));
          
          if (missingColumns.length > 0) {
            this.setState({ 
              msg: `Missing required columns: ${missingColumns.join(', ')}. Expected: Email Address, Role, Group` 
            });
            return;
          }

          const processedData = result.data.map(row => {
            const email = row['Email Address']?.trim();
            const role = row['Role']?.trim().toLowerCase();
            const groupTitle = row['Group']?.trim();

            const group = this.props.groups?.find(g => 
              g.title.toLowerCase() === groupTitle.toLowerCase()
            );

            return {
              email,
              role: ['student', 'instructor', 'admin'].includes(role) ? role : 'student',
              group: role !== 'admin' && group ? group._id : undefined
            };
          }).filter(item => item.email);

          this.setState({ 
            csvData: processedData,
            msg: null 
          });
        },
        error: (error) => {
          this.setState({ 
            msg: 'Error reading CSV file: ' + error.message 
          });
        }
      });
    } else {
      this.setState({ 
        msg: 'Please select a valid CSV file.' 
      });
    }
  };

  processBulkInvitations = () => {
    if (this.state.csvData.length === 0) {
      this.setState({ msg: 'No valid data to process.' });
      return;
    }

    this.setState({ bulkLoading: true });
    
    this.props.addBulkInvitations(this.state.csvData);
    
    setTimeout(() => {
      this.setState({ 
        bulkLoading: false,
        bulkModal: false 
      });
      this.loadInvitations();
    }, 2000);
  };

  onChange = e => {
    this.setState({ [e.target.name]: e.target.value });
    
    if (e.target.name === 'email') {
      this.validateEmail(e.target.value);
    }
  };

  validateEmail = async (email) => {
    if (!email || email.trim() === '') {
      this.setState({ emailError: null, isCheckingEmail: false });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      this.setState({ emailError: 'Please enter a valid email format', isCheckingEmail: false });
      return;
    }

    this.setState({ isCheckingEmail: true, emailError: null });

    try {
      await this.props.checkEmailExists(email);
      this.setState({ emailError: null, isCheckingEmail: false });
    } catch (error) {
      if (error.exists) {
        let errorMessage = error.msg;
        if (error.type === 'user') {
          errorMessage = 'This email is already registered by an existing user';
        } else if (error.type === 'invitation') {
          errorMessage = 'This email already has a pending invitation';
        }
        
        this.setState({ 
          emailError: errorMessage, 
          isCheckingEmail: false 
        });
      } else {
        this.setState({ emailError: null, isCheckingEmail: false });
      }
    }
  };

  onSubmit = e => {
    e.preventDefault();
    
    if (this.state.emailError) {
      this.setState({ msg: this.state.emailError });
      return;
    }
    
    if (this.state.role !== 'admin' && this.state.role !== 'platform_admin' && !this.state.group) {
      this.setState({ msg: 'Group is required for non-admin users.' });
      return;
    }

    const invitationData = {
      email: this.state.email,
      role: this.state.role,
      ...(this.state.role !== 'admin' && this.state.role !== 'platform_admin' && { group: this.state.group })
    };

    if (this.state.editMode) {
      this.props.updateInvitation(this.state.editingInvitation._id, invitationData);
    } else {
      this.props.addInvitation(invitationData);
    }
    
    this.toggle();
    setTimeout(() => this.loadInvitations(), 500);
  };

  render() {
    const { invitations, loading, pagination, filterValues } = this.props.invitation;
    const { currentPage, statusFilter, roleFilter, selectedEmails, selectedGroups, startDate, endDate } = this.state;

    if (loading) {
      return <Spinner />;
    }

    const hasActiveFilters = statusFilter.length > 0 || roleFilter.length > 0 || 
                            selectedEmails.length > 0 || selectedGroups.length > 0 || 
                            startDate || endDate;

    const getRoleBadgeClass = (role) => {
      switch (role) {
        case 'admin':
          return 'bg-red-100 text-red-800';
        case 'instructor':
          return 'bg-orange-100 text-orange-800';
        case 'student':
        default:
          return 'bg-blue-100 text-blue-800';
      }
    };

    // Prepare filter data
    const emailOptions = filterValues.emails?.map(email => ({ _id: email, title: email })) || [];
    const groupOptions = filterValues.groups || [];
    const statusOptions = filterValues.statuses?.map(status => ({ 
      _id: status, 
      title: status.charAt(0).toUpperCase() + status.slice(1) 
    })) || [];
    const roleOptions = filterValues.roles?.map(role => ({ 
      _id: role, 
      title: role.charAt(0).toUpperCase() + role.slice(1) 
    })) || [];

    // Pagination controls component
    const PaginationControls = () => (
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 py-4">
        <div className="text-sm text-gray-600">
          Showing {invitations.length > 0 ? ((currentPage - 1) * pagination.limit + 1) : 0} to{' '}
          {Math.min(currentPage * pagination.limit, pagination.total)} of {pagination.total} invitations
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => this.handlePageChange(currentPage - 1)}
            disabled={!pagination.hasPrevPage}
            className={`p-2 rounded-lg border ${
              pagination.hasPrevPage
                ? 'border-[#1086a4] text-[#1086a4] hover:bg-[#1086a4] hover:text-white'
                : 'border-gray-300 text-gray-400 cursor-not-allowed'
            } transition-colors`}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span className="text-sm text-gray-700 px-4">
            Page {currentPage} of {pagination.totalPages || 1}
          </span>
          <button
            onClick={() => this.handlePageChange(currentPage + 1)}
            disabled={!pagination.hasNextPage}
            className={`p-2 rounded-lg border ${
              pagination.hasNextPage
                ? 'border-[#1086a4] text-[#1086a4] hover:bg-[#1086a4] hover:text-white'
                : 'border-gray-300 text-gray-400 cursor-not-allowed'
            } transition-colors`}
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    );

    return (
      <div className="mt-8">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
          <h2 className="text-2xl font-bold text-[#054581]">Invitations Management</h2>
          {this.props.isAuthenticated && (
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
              <button
                className="bg-[#1086a4] text-white px-4 sm:px-6 py-2 rounded-lg font-semibold shadow hover:bg-[#054581] transition-colors flex items-center justify-center"
                onClick={this.toggle}
              >
                Add new invitation
              </button>
              <button
                className="bg-[#1086a4] text-white px-4 sm:px-6 py-2 rounded-lg font-semibold shadow hover:bg-[#054581] transition-colors flex items-center justify-center"
                onClick={this.toggleBulkModal}
              >
                <Upload className="w-5 h-5 mr-2" />
                Bulk invitations
              </button>
            </div>
          )}
        </div>

        {/* Filters Section */}
        <div className="mb-6 space-y-4">
          {/* Status and Role Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <MultiSelectSearchFilter
              items={statusOptions}
              selectedItems={statusFilter}
              onSelectionChange={this.handleStatusChange}
              title="Status"
              placeholder="Search status..."
              itemIcon={Clock}
            />
            <MultiSelectSearchFilter
              items={roleOptions}
              selectedItems={roleFilter}
              onSelectionChange={this.handleRoleChange}
              title="Role"
              placeholder="Search role..."
              itemIcon={Layers}
            />
          </div>

          {/* Date Range Filter */}
          <div className="bg-gradient-to-r from-gray-50 to-blue-50 p-4 rounded-lg border border-gray-200">
            <div className="flex items-center gap-2 mb-3 pb-3 border-b border-gray-200">
              <Calendar className="w-4 h-4 text-[#1086a4]" />
              <span className="text-sm font-medium text-[#054581]">Filter by Created Date</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">From</label>
                <input
                  type="date"
                  name="startDate"
                  value={startDate}
                  onChange={this.handleDateChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">To</label>
                <input
                  type="date"
                  name="endDate"
                  value={endDate}
                  onChange={this.handleDateChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Email and Group Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <MultiSelectSearchFilter
              items={emailOptions}
              selectedItems={selectedEmails}
              onSelectionChange={this.handleEmailChange}
              title="Email"
              placeholder="Search emails..."
              itemIcon={AtSign}
            />
            <MultiSelectSearchFilter
              items={groupOptions}
              selectedItems={selectedGroups}
              onSelectionChange={this.handleGroupChange}
              title="Group"
              placeholder="Search groups..."
              itemIcon={Layers}
            />
          </div>

          {/* Clear All Filters Button */}
          {hasActiveFilters && (
            <div className="flex justify-end">
              <button
                onClick={this.clearAllFilters}
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors shadow-sm"
              >
                <X className="w-4 h-4" />
                Clear All Filters
              </button>
            </div>
          )}
        </div>

        {/* Pagination - Top */}
        <PaginationControls />

        {/* Mobile View */}
        <div className="md:hidden space-y-4">
          {invitations.map(({ _id, email, role, status, group, createdAt }) => (
            <div key={_id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#1086a4] bg-opacity-10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-[#1086a4]" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-[#054581] mb-2">
                      <div className="flex items-center gap-2">
                        <AtSign className="w-4 h-4 text-[#1086a4]" />
                        <span className="text-[#1e2d40] truncate">{email}</span>
                      </div>
                    </h3>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm">
                        <Clock className="w-4 h-4 text-[#1086a4]" />
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          status === 'pending' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {status.charAt(0).toUpperCase() + status.slice(1)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-2 text-sm">
                        <div className="flex items-center gap-2 min-w-0">
                          <Layers className="w-4 h-4 text-[#1086a4] flex-shrink-0" />
                          <span className="text-[#1e2d40] truncate">
                            {group ? group.title : 'No group assigned'}
                          </span>
                        </div>
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full flex-shrink-0 ${getRoleBadgeClass(role)}`}>
                          {role.charAt(0).toUpperCase() + role.slice(1)}
                        </span>
                      </div>
                      {createdAt && (
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <Calendar className="w-3 h-3" />
                          {new Date(createdAt).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {this.props.isAuthenticated && (
                <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-2">
                  {status === 'pending' && (
                    <button
                      onClick={() => this.onEditClick({ _id, email, role, status, group })}
                      className="p-2 text-[#1086a4] hover:text-[#054581] transition-colors rounded-lg hover:bg-white"
                    >
                      <Edit2 size={18} />
                    </button>
                  )}
                  <button
                    onClick={() => this.onDeleteClick(_id)}
                    className="p-2 text-red-500 hover:text-red-700 transition-colors rounded-lg hover:bg-white"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Desktop View */}
        <div className="hidden md:block w-full overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#054581] uppercase tracking-wider">
                  Email Address
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#054581] uppercase tracking-wider">
                  Role
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#054581] uppercase tracking-wider">
                  Group
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#054581] uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#054581] uppercase tracking-wider">
                  Created
                </th>
                {this.props.isAuthenticated && (
                  <th scope="col" className="relative px-6 py-3 w-24">
                    <span className="sr-only">Actions</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {invitations.map(({ _id, email, role, status, group, createdAt }) => (
                <tr key={_id} className="hover:bg-gray-50 transition-colors duration-150">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleBadgeClass(role)}`}>
                      {role.charAt(0).toUpperCase() + role.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">
                      {group ? group.title : 'No group assigned'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      status === 'pending' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {createdAt ? new Date(createdAt).toLocaleDateString() : '-'}
                  </td>
                  {this.props.isAuthenticated && (
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-3">
                        {status === 'pending' && (
                          <button
                            onClick={() => this.onEditClick({ _id, email, role, status, group })}
                            className="text-[#1086a4] hover:text-[#054581] transition-colors"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => this.onDeleteClick(_id)}
                          className="text-red-500 hover:text-red-700 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination - Bottom */}
        <PaginationControls />

        {/* Modal */}
        {this.state.modal && (
          <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center">
            <div className="relative bg-white rounded-lg w-full max-w-md mx-4 p-6 border-2 border-[#1086a4]">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-[#054581]">
                  {this.state.editMode ? 'Edit Invitation' : 'Add New Invitation'}
                </h3>
                <button 
                  onClick={this.toggle} 
                  className="text-gray-400 hover:text-[#1086a4] focus:outline-none transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {this.state.msg && (
                <div className="mb-4 p-3 bg-red-50 border border-red-400 text-red-700 rounded">
                  {this.state.msg}
                </div>
              )}

              <form onSubmit={this.onSubmit} className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-[#054581] mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <input
                      type="email"
                      name="email"
                      id="email"
                      placeholder="Enter email address"
                      value={this.state.email}
                      className={`w-full px-3 py-2 bg-[#f9fafb] border rounded-md text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:border-transparent transition-colors ${
                        this.state.emailError 
                          ? 'border-red-400 focus:ring-red-400' 
                          : 'border-[#1086a4] focus:ring-[#1086a4]'
                      }`}
                      onChange={this.onChange}
                      required
                    />
                    {this.state.isCheckingEmail && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#1086a4]"></div>
                      </div>
                    )}
                  </div>
                  {this.state.emailError && (
                    <p className="mt-1 text-sm text-red-600">{this.state.emailError}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="role" className="block text-sm font-medium text-[#054581] mb-2">
                    Role
                  </label>
                  <select
                    name="role"
                    id="role"
                    className="w-full px-3 py-2 bg-[#f9fafb] border border-[#1086a4] rounded-md text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-transparent transition-colors"
                    onChange={this.onChange}
                    value={this.state.role}
                    required
                  >
                    <option value="student">Student</option>
                    <option value="instructor">Instructor</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>

                {this.state.role !== 'admin' && this.state.role !== 'platform_admin' && (
                  <div>
                    <label htmlFor="group" className="block text-sm font-medium text-[#054581] mb-2">
                      Group <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="group"
                      id="group"
                      className="w-full px-3 py-2 bg-[#f9fafb] border border-[#1086a4] rounded-md text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-transparent transition-colors"
                      onChange={this.onChange}
                      value={this.state.group}
                      required
                    >
                      <option value="">Select a group</option>
                      {this.props.groups && this.props.groups.map(group => (
                        <option key={group._id} value={group._id}>
                          {group.title}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={this.toggle}
                    className="flex-1 py-2 px-4 border border-[#1086a4] rounded-md text-[#1086a4] font-medium hover:bg-[#f9fafb] focus:outline-none focus:ring-2 focus:ring-[#1086a4] transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 py-2 px-4 bg-[#1086a4] text-white rounded-md font-medium hover:bg-[#054581] focus:outline-none focus:ring-2 focus:ring-[#1086a4] transition-colors"
                  >
                    {this.state.editMode ? 'Update Invitation' : 'Add Invitation'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Bulk Invitation Modal */}
        {this.state.bulkModal && (
          <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center">
            <div className="relative bg-white rounded-lg w-full max-w-2xl mx-4 p-6 border-2 border-[#1086a4]">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-[#054581]">Bulk Upload Invitations</h3>
                <button 
                  onClick={this.toggleBulkModal} 
                  className="text-gray-400 hover:text-[#1086a4] focus:outline-none transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="mb-6 p-4 bg-[#f9fafb] rounded-lg border border-[#1086a4]">
                <h4 className="text-[#054581] font-semibold mb-2">CSV File Format</h4>
                <p className="text-gray-700 text-sm mb-2">
                  Your CSV file should have exactly three columns with these headers:
                </p>
                <ul className="text-gray-700 text-sm list-disc list-inside space-y-1">
                  <li><strong>Email Address</strong> - The user's email address</li>
                  <li><strong>Role</strong> - Either 'student', 'instructor', or 'admin'</li>
                  <li><strong>Group</strong> - The group name (not required for admin role)</li>
                </ul>
                <p className="text-gray-500 text-xs mt-2">
                  Example: user@example.com, student, Math Group
                </p>
              </div>

              {this.state.msg && (
                <div className="mb-4 p-3 bg-red-50 border border-red-400 text-red-700 rounded">
                  {this.state.msg}
                </div>
              )}

              <div className="mb-6">
                <label htmlFor="csvFile" className="block text-sm font-medium text-[#054581] mb-2">
                  Upload CSV File
                </label>
                <input
                  type="file"
                  id="csvFile"
                  accept=".csv"
                  onChange={this.handleFileUpload}
                  className="w-full px-3 py-2 bg-[#f9fafb] border border-[#1086a4] rounded-md text-gray-900 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-[#1086a4] file:text-white hover:file:bg-[#054581] transition-colors"
                />
              </div>

              {this.state.csvData.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-[#054581] font-semibold mb-3">
                    Preview ({this.state.csvData.length} invitations)
                  </h4>
                  <div className="max-h-40 overflow-y-auto bg-gray-50 rounded-lg p-3 border border-gray-200">
                    {this.state.csvData.slice(0, 5).map((item, index) => (
                      <div key={index} className="text-sm text-gray-700 mb-1">
                        {item.email} - {item.role} - {item.group ? 
                          this.props.groups?.find(g => g._id === item.group)?.title || 'Unknown Group' : 
                          'No Group'
                        }
                      </div>
                    ))}
                    {this.state.csvData.length > 5 && (
                      <div className="text-sm text-gray-500">
                        ... and {this.state.csvData.length - 5} more
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={this.toggleBulkModal}
                  className="flex-1 py-2 px-4 border border-[#1086a4] rounded-md text-[#1086a4] font-medium hover:bg-[#f9fafb] focus:outline-none focus:ring-2 focus:ring-[#1086a4] transition-colors"
                  disabled={this.state.bulkLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={this.processBulkInvitations}
                  disabled={this.state.csvData.length === 0 || this.state.bulkLoading}
                  className="flex-1 py-2 px-4 bg-[#1086a4] text-white rounded-md font-medium hover:bg-[#054581] focus:outline-none focus:ring-2 focus:ring-[#1086a4] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {this.state.bulkLoading ? 'Processing...' : 'Create Invitations'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {this.state.deleteModal && (
          <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center">
            <div className="relative bg-white rounded-lg w-full max-w-md mx-4 p-6 border-2 border-[#1086a4]">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-[#054581]">Confirm Deletion</h3>
                <button 
                  onClick={this.handleDeleteCancel} 
                  className="text-gray-400 hover:text-[#1086a4] focus:outline-none transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              <p className="text-gray-700 text-sm mb-4">
                Are you sure you want to delete this invitation? This action cannot be undone.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={this.handleDeleteCancel}
                  className="flex-1 py-2 px-4 border border-[#1086a4] rounded-md text-[#1086a4] font-medium hover:bg-[#f9fafb] focus:outline-none focus:ring-2 focus:ring-[#1086a4] transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={this.handleDeleteConfirm}
                  className="flex-1 py-2 px-4 bg-red-500 text-white rounded-md font-medium hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

const mapStateToProps = state => ({
  invitation: state.invitation,
  isAuthenticated: state.auth.isAuthenticated,
  groups: state.group.groups
});

export default connect(
  mapStateToProps,
  { getInvitations, deleteInvitation, addInvitation, updateInvitation, addBulkInvitations, getGroups, checkEmailExists, getInvitationFilterValues }
)(InvitationsTab);
